export type PersonalNotification = {
  id: number
  type: string
  source: string
  source_id: string | null
  payload: {
    module?: string
    flow_id?: number
    flow_name?: string
    account_id?: number
    offer_count?: number
    deep_link?: string
    [key: string]: unknown
  }
  read_at: string | null
  created_at: string
}

export type UnreadCount = {
  personal: number
  news: number
}

export type NotificationTypeKey = 'generation_ready' | 'generation_failed'

export type NotificationChannelKey = 'in_app' | 'email'

export type AdsNotificationType =
  | 'ads_active'
  | 'cooperation_ended'
  | 'decisions_pending'
  | 'sync_failed'
  | 'invite_pending'

export type NotificationPreferenceModule =
  | 'description'
  | 'title'
  | 'parameter'
  | 'graphics'
  | 'comprehensive'
  | 'listing'

export type NotificationPreferencesMatrix = Record<
  NotificationPreferenceModule,
  Record<NotificationTypeKey, boolean>
>

export type AdsPreferencesCells = Record<AdsNotificationType, boolean>

export type NotificationPreferencesResponse = {
  in_app: NotificationPreferencesMatrix
  email: NotificationPreferencesMatrix
  notify_email: string
  ads: AdsPreferencesCells
}

export type NotificationPreferencesUpdate = {
  in_app: NotificationPreferencesMatrix
  email: NotificationPreferencesMatrix
  notify_email?: string
  ads?: AdsPreferencesCells
}

/** Ordered AI modules for preference UI — keep in sync with backend PREFERENCE_MODULES. */
export const PREFERENCE_MODULES: NotificationPreferenceModule[] = [
  'description',
  'title',
  'parameter',
  'graphics',
  'comprehensive',
  'listing',
]

export const PREFERENCE_TYPES: NotificationTypeKey[] = [
  'generation_ready',
  'generation_failed',
]

export const PREFERENCE_CHANNELS: NotificationChannelKey[] = ['in_app', 'email']

/** Seller-facing Ads mute toggles (invite_pending is admin-only in UI). */
export const ADS_SELLER_PREFERENCE_TYPES: AdsNotificationType[] = [
  'ads_active',
  'cooperation_ended',
  'decisions_pending',
  'sync_failed',
]

export const ADS_ADMIN_PREFERENCE_TYPE: AdsNotificationType = 'invite_pending'

export const ADS_PREFERENCE_TYPES: AdsNotificationType[] = [
  ...ADS_SELLER_PREFERENCE_TYPES,
  ADS_ADMIN_PREFERENCE_TYPE,
]

export const DEFAULT_ADS_PREFERENCES: AdsPreferencesCells = {
  ads_active: true,
  cooperation_ended: true,
  decisions_pending: true,
  sync_failed: true,
  invite_pending: true,
}

export const NOTIFICATIONS_UNREAD_KEY = ['notifications', 'unread-count'] as const
export const NOTIFICATIONS_PERSONAL_KEY = ['notifications', 'personal'] as const
export const NOTIFICATIONS_PREFERENCES_KEY = ['notifications', 'preferences'] as const

export function emptyMatrix(defaultEnabled: boolean): NotificationPreferencesMatrix {
  return PREFERENCE_MODULES.reduce((acc, module) => {
    acc[module] = {
      generation_ready: defaultEnabled,
      generation_failed: defaultEnabled,
    }
    return acc
  }, {} as NotificationPreferencesMatrix)
}

export function cloneMatrix(matrix: NotificationPreferencesMatrix): NotificationPreferencesMatrix {
  return PREFERENCE_MODULES.reduce((acc, module) => {
    acc[module] = { ...matrix[module] }
    return acc
  }, {} as NotificationPreferencesMatrix)
}

export function moduleTaskLabelKey(module: string | undefined): string {
  switch (module) {
    case 'description':
      return 'notifications.tasks.descriptionsFlow'
    case 'title':
      return 'notifications.tasks.titlesFlow'
    case 'parameter':
      return 'notifications.tasks.parametersFlow'
    case 'graphics':
      return 'notifications.tasks.graphicsFlow'
    case 'comprehensive':
      return 'notifications.tasks.comprehensiveFlow'
    case 'listing':
      return 'notifications.tasks.listingFlow'
    case 'ads':
      return 'notifications.tasks.ads'
    default:
      return 'notifications.tasks.genericFlow'
  }
}
